<template>
    <ValidationObserver v-slot="{ handleSubmit }">
        <form
            @submit.prevent="handleSubmit(downloadCode)"
            class="flex flex-col w-full py-4 md:px-4"
        >
            <div id="OptParque" class="form-folio">
                <ValidationProvider
                    :name="tagInputLabel"
                    rules="required"
                    v-slot="{ errors }"
                >
                    <label class="font-bold" :for="tagInputLabel">
                        {{ tagInputLabel }}<span v-if="shouldAppendTagLabelColon">:</span>
                    </label>

                    <multiselect
                        v-model="tagList"
                        :options="options"
                        :multiple="true"
                        :taggable="true"
                        @tag="addTag"
                        @input="enforceMaxTags"
                        :showNoResults="false"
                        :showNoOptions="false"
                        :tagPlaceholder="$t('Forms.add_tag')"
                        placeholder=""
                        :selectLabel="$t('Forms.press_enter_select')"
                        open-direction="bottom"
                        :class="{ error: tagError }"
                        :close-on-select="maxTagsAllowed === 1"
                        :preserve-search="true"
                    />

                    <InvalidFormatMessage>
                        {{ tagError }}
                    </InvalidFormatMessage>
                    <InvalidFormatMessage>
                        {{ errors[0] }}
                    </InvalidFormatMessage>
                </ValidationProvider>

                <div class="flex -mx-2">
                    <div class="my-2 mx-2 w-full md:w-1/2">
                        <ValidationProvider
                            :name="visitDateLabel"
                            rules="required"
                            v-slot="{ errors }"
                        >
                            <label class="font-bold" :for="visitDateLabel">
                                {{ visitDateLabel }}<span v-if="shouldAppendVisitDateLabelColon">:</span>
                            </label>

                            <date-picker
                                v-model="GetPhotos.visitDate"
                                type="date"
                                placeholder="YYYY-MM-DD"
                                :default-value="calendarDefaultDate"
                                :disabled-date="disabledDate"
                                :disabled="isSingleConfiguredDate"
                                value-type="format"
                                format="YYYY-MM-DD"
                                :editable="false"
                                :clearable="false"
                            />

                            <InvalidFormatMessage>
                                {{ errors[0] }}
                            </InvalidFormatMessage>
                        </ValidationProvider>
                    </div>
                </div>
            </div>

            <button
                type="submit"
                class="w-full flex justify-center rounded-full py-3 px-4 mb-1 mt-2 bg-red-500 text-white cursor-pointer font-semibold mx-auto"
            >
                {{ $t("Forms.add_code") }}
            </button>
        </form>
    </ValidationObserver>
</template>

<script>
import { GetApiErrorMessage } from "@helpers/apiErrorHelper";
import { IsNormalizedTag, NormalizeTag } from "@helpers/tagHelper";
import { IsDateYmd } from "@helpers/dateHelper";
let code = document.documentElement.lang;
import "@validators/index";

import InvalidFormatMessage from "@components/common/forms/InvalidFormatMessage.vue";

import AlbumsByTagMix from "@mixins/albums/GetAlbumsByTag";
import methodUser from "@mixins/methodUser";
import cartMx from "@mixins/Cart";
import Multimedia from "@mixins/Multimedia";

import "vue-multiselect/dist/vue-multiselect.min.css";
import Multiselect from "vue-multiselect";
import DatePicker from "vue2-datepicker";
import "vue2-datepicker/index.css";
import "vue2-datepicker/locale/es";
DatePicker.locale(code);

export default {
    name: "AddDynamicPark",
    props: {
        eventConfig: {
            type: Object,
            default: () => ({}),
        },
    },
    mixins: [AlbumsByTagMix, methodUser, Multimedia, cartMx],
    components: {
        DatePicker,
        Multiselect,
        InvalidFormatMessage,
    },
    data: () => ({
        tagError: "",
        errors: [],
        downloadCodes: [],
        option: 0,
        GetPhotos: {
            visitDate: "",
            tag: "",
        },
        Parks: [{ value: null, text: "Seleccione una opción" }],
        Hotels: [{ value: null, text: "Seleccione una opción" }],
        tagList: [],
        options: [],
    }),
    computed: {
        GetParks() {
            return this.$store.getters.GetParks;
        },
        parkId() {
            const value = Number(this.eventConfig?.ParkId);
            return Number.isInteger(value) && value > 0 ? value : 0;
        },
        serviceId() {
            return typeof this.eventConfig?.ServiceId === "string"
                ? this.eventConfig.ServiceId.trim()
                : "";
        },
        maxTagsAllowed() {
            const configuredMax = Number(this.eventConfig?.MaxTags);

            if (Number.isInteger(configuredMax) && configuredMax > 0) {
                return configuredMax;
            }

            // Preserve the historical generic limit unless EventSalesConfig
            // explicitly defines a smaller limit (Bravest uses MaxTags = 1).
            return 10;
        },
        maxTagsValidationMessage() {
            return this.maxTagsAllowed === 1
                ? this.$t("Forms.only_one_competitor_number")
                : this.$t("Forms.max_ten_tags");
        },
        validDates() {
            const configuredDates = Array.isArray(this.eventConfig?.Dates)
                ? this.eventConfig.Dates
                : [];

            return [...new Set(configuredDates)]
                .filter((date) => IsDateYmd(date))
                .filter((date) => this.isRealDate(date));
        },
        isConfiguredEvent() {
            return this.validDates.length > 0 && this.serviceId !== "";
        },
        usesRestrictedCalendar() {
            return this.isConfiguredEvent;
        },
        isSingleConfiguredDate() {
            return this.isConfiguredEvent && this.validDates.length === 1;
        },
        shouldAppendTagLabelColon() {
            return !this.isConfiguredEvent;
        },
        shouldAppendVisitDateLabelColon() {
            return !this.isConfiguredEvent;
        },
        tagInputLabel() {
            return this.isConfiguredEvent
                ? this.$t("Forms.bravest_competitor_number_label")
                : this.$t("Forms.bracelet_hull_subgroup");
        },
        visitDateLabel() {
            return this.isConfiguredEvent
                ? this.$t("Forms.bravest_participation_date_label")
                : this.$t("Forms.visit_date");
        },
        calendarDefaultDate() {
            if (this.validDates.length > 0) {
                return this.toLocalDate(this.validDates[0]);
            }

            return new Date();
        },
        eventSearchContext() {
            return {
                EventId: this.eventConfig?.EventId || null,
                ParkId: this.parkId,
                Dates: this.validDates,
                ServiceId: this.serviceId || null,
                UseEventDate: this.eventConfig?.UseEventDate === true,
                MaxTags: this.maxTagsAllowed,
                isEvent: this.isConfiguredEvent,
                AgentDetails : this.eventConfig?.AgentDetails || null,
            };
        },
    },
    watch: {
        eventConfig: {
            immediate: true,
            deep: true,
            handler() {
                this.initializeVisitDate();
                this.tagList = [];
                this.tagError = "";
            },
        },
    },
    methods: {
        /**
         * Selects the first configured event date or clears regular searches.
         *
         * A single event date remains selected and locked. Events with two or
         * more dates start on the first configured date and allow only dates
         * declared by the event configuration.
         *
         * @returns {void}
         */
        initializeVisitDate() {
            this.GetPhotos.visitDate = this.isConfiguredEvent
                ? this.validDates[0]
                : "";
        },
        toLocalDate(date) {
            const [year, month, day] = String(date).split("-").map(Number);
            return new Date(year, month - 1, day);
        },
        isRealDate(date) {
            const parsed = this.toLocalDate(date);
            const [year, month, day] = String(date).split("-").map(Number);

            return (
                parsed.getFullYear() === year &&
                parsed.getMonth() === month - 1 &&
                parsed.getDate() === day
            );
        },
        disabledDate(date) {
            if (this.usesRestrictedCalendar) {
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, "0");
                const day = String(date.getDate()).padStart(2, "0");
                return !this.validDates.includes(`${year}-${month}-${day}`);
            }

            return this.disabledBeforeTodayAndAfterAWeek(date);
        },
        addTag(newTag) {
            this.tagError = "";
            const cleanedTag = this.cleanTag(newTag);

            if (!cleanedTag) {
                Vue.$toast.open({ message: this.$t("Forms.invalid_tag"), type: "error" });
                return;
            }

            if (this.tagList.length >= this.maxTagsAllowed) {
                Vue.$toast.open({ message: this.maxTagsValidationMessage, type: "error" });
                return;
            }

            if (cleanedTag.length > 25) {
                Vue.$toast.open({ message: this.$t("Forms.max_tag_characters"), type: "error" });
                return;
            }

            if (this.tagList.includes(cleanedTag)) {
                Vue.$toast.open({
                    message: this.$t("Forms.duplicate_tag", { tag: cleanedTag }),
                    type: "error",
                });
                return;
            }

            this.tagList.push(cleanedTag);
        },
        enforceMaxTags(values) {
            if (!Array.isArray(values)) {
                this.tagList = [];
                return;
            }

            if (values.length <= this.maxTagsAllowed) {
                return;
            }

            this.tagList = values.slice(0, this.maxTagsAllowed);

            Vue.$toast.open({
                message: this.maxTagsValidationMessage,
                type: "error",
            });
        },
        cleanTag(tag) {
            return NormalizeTag(tag, { allowSpaces: true });
        },
        validateAllTags() {
            if (this.tagList.length === 0) {
                return { isValid: false, message: this.$t("Forms.add_at_least_one_tag") };
            }

            if (this.tagList.length > this.maxTagsAllowed) {
                return { isValid: false, message: this.maxTagsValidationMessage };
            }

            const invalidTags = this.tagList.filter((tag) => !this.isValidTag(tag));
            if (invalidTags.length > 0) {
                return {
                    isValid: false,
                    message: this.$t("Forms.invalid_tags", {
                        tags: invalidTags.join(", "),
                    }),
                };
            }

            return { isValid: true, message: "" };
        },
        isValidTag(tag) {
            return IsNormalizedTag(tag) && tag.length <= 25;
        },
        hasCode(visitDate) {
            const requestedTags = [...this.tagList].sort();

            return Object.values(this.$store.state.albumes).some((album) => {
                const albumTags = Array.isArray(album.Tags) ? [...album.Tags].sort() : [];
                return (
                    album.Type === 1 &&
                    Number(album.ParkId) === this.parkId &&
                    album.VisitDate === visitDate &&
                    album.ServiceId === (this.serviceId || null) &&
                    requestedTags.length === albumTags.length &&
                    requestedTags.every((tag, index) => tag === albumTags[index])
                );
            });
        },
        validateVisitDate() {
            const visitDate = String(this.GetPhotos.visitDate || "");

            if (!visitDate) {
                return false;
            }

            return !this.isConfiguredEvent || this.validDates.includes(visitDate);
        },
        async downloadCode() {
            try {
                this.$store.dispatch("setLoading", true);
                this.msgApi.error = false;
                this.msgApi.message = "";
                this.tagError = "";

                const tagsValidation = this.validateAllTags();
                if (!tagsValidation.isValid) {
                    this.tagError = tagsValidation.message;
                    return;
                }

                if (!this.validateVisitDate()) {
                    Vue.$toast.open({
                        message: this.$t("Forms.invalid_competition_date"),
                        type: "error",
                    });
                    return;
                }

                if (!this.parkId) {
                    throw new Error("Dynamic search ParkId is not configured");
                }

                const visitDate = String(this.GetPhotos.visitDate);
                if (this.hasCode(visitDate)) {
                    Vue.$toast.open({
                        message: this.$t("Forms.exist_code"),
                        type: "info",
                    });
                    return;
                }

                const groupedTags = this.GroupParksByDate(
                    this.tagList,
                    visitDate,
                    this.parkId,
                    this.serviceId || null,
                    this.maxTagsAllowed
                );

                await this.GetAlbumByTag(
                    groupedTags,
                    this.parkId,
                    visitDate,
                    false,
                    this.eventSearchContext
                );

                this.$store.dispatch("setModalAddAlbum", false);
            } catch (error) {
                const message = GetApiErrorMessage(
                    error,
                    this.$t("messages.album_error_add")
                );
                this.msgApi.message = message;

                Vue.$toast.open({
                    message,
                    type: "error",
                });
                this.msgApi.error = true;
            } finally {
                this.$store.dispatch("setLoading", false);
            }
        },
    },
};
</script>
